import { StrictMode } from 'react'
import './i18n'
import { createRoot } from 'react-dom/client'
import './index.css'
import './styles/admin-tokens.css'
import App from './App.tsx'
import { isChunkLoadError, reloadForStaleChunk } from '@/lib/chunkReload'

// ── Stale-chunk self-heal ────────────────────────────────────────────────────
// After a new deploy, every code chunk gets a new hashed filename. A browser
// tab opened BEFORE the deploy still asks for the OLD filename, which no longer
// exists — so a lazy route fails with "Failed to fetch dynamically imported
// module". We catch that failure as early as possible and reload once to pick up
// the fresh HTML + fresh chunk names. A cooldown guard inside reloadForStaleChunk
// prevents any reload loop if a chunk is genuinely missing.

// 1) Vite's own signal for a failed preload / dynamic import.
window.addEventListener('vite:preloadError', (event) => {
  if (event && typeof event.preventDefault === 'function') {
    event.preventDefault();
  }
  reloadForStaleChunk();
});

// 2) Safety nets — any unhandled rejection or script error that looks like a
//    stale chunk is treated the same way, even if it escapes the router.
window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event?.reason)) {
    reloadForStaleChunk();
  }
});

window.addEventListener('error', (event) => {
  if (isChunkLoadError(event?.error) || isChunkLoadError(event?.message)) {
    reloadForStaleChunk();
  }
});

// Prerendered routes ship a crawl-time HTML shell inside #root. We use
// createRoot (not hydrateRoot) on purpose: even the prerendered markup that
// comes from full SSR can differ from the client render (randomised imagery,
// CMS data resolved in effects, i18n timing), so React replaces the shell
// instead of hydrating it. That trades a brief flash for zero hydration
// mismatches — the correct call once an app renders non-deterministically.
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
